import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * One search row as the card's modal renders it — the §4.1.11 `Pharmacy` fields
 * PD-1386 displays or stores, nothing more. **No FHIR at the boundary** (`AGENTS.md`),
 * and deliberately no DoseSpot internals beyond the two identifiers the snapshot
 * keeps: `pharmacyId` (environment-scoped) and `ncpdpId` (the stable key).
 */
const pharmacyRowSchema = z.object({
  pharmacyId: z.number().int().positive(),
  ncpdpId: z.string(),
  storeName: z.string(),
  address1: z.string(),
  address2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string(),
  fax: z.string().optional(),
  /**
   * `PharmacySpecialties` — the retail/mail-order marker, `null` on many real rows
   * (staging probes: bare CVS entries), so absence means DoseSpot said nothing.
   */
  specialties: z.array(z.string()).optional(),
})

/**
 * Proxies DoseSpot's pharmacy directory (§3.13.2) for the Demographics card's search
 * modal — the search always proxies DoseSpot; there is no BeHuman pharmacy directory.
 *
 * DoseSpot enforces its own ≥3-character minimums via `ModelState` 400s, which arrive
 * here as a 400 naming the offending fields; the modal mirrors the minimums as hints
 * only. Paging is the server's fixed 20 rows per page, surfaced as `totalCount` +
 * `hasNext` for the modal's "Load more".
 */
export const doseSpotPharmacySearch = oc
  .route({ method: 'POST', path: '/v2/dosespot/pharmacies/search' })
  .input(
    z.object({
      name: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zip: z.string().optional(),
      phoneOrFax: z.string().optional(),
      /** §4.2.7, the two members the card filters by — omitted means all types. */
      specialty: z.enum(['Retail', 'MailOrder']).optional(),
      /** 1-based. */
      pageNumber: z.number().int().min(1).optional(),
    }),
  )
  .output(
    z.object({
      pharmacies: z.array(pharmacyRowSchema),
      /** Total matches across all pages — capped at 100 on DoseSpot's zip-expansion path. */
      totalCount: z.number(),
      hasNext: z.boolean(),
    }),
  )

/**
 * Stores the chosen search row as the patient's default pharmacy — one complex
 * extension on `Patient`, snapshot copied at add-time (PD-1386 / ticket 02).
 *
 * **Never calls DoseSpot**: the association is asserted by the next Rx Prescribe
 * launch, not by this save, so the card works when DoseSpot is down and for patients
 * DoseSpot has never seen.
 */
export const doseSpotSetDefaultPharmacy = oc
  .route({ method: 'POST', path: '/v2/dosespot/patients/{patientId}/default-pharmacy' })
  .input(z.object({ patientId: z.string().min(1) }).extend(pharmacyRowSchema.shape))
  .output(
    z.object({
      /** The stored id, as the extension keeps it — confirms the write landed. */
      pharmacyId: z.string(),
    }),
  )

/**
 * Clears the stored default. **DoseSpot is not told** (ticket 03's bare-remove rule):
 * if the patient was already associated in DoseSpot, the pharmacy may stay selected
 * there until it is replaced or changed during prescribing — the card's remove-confirm
 * copy states exactly this.
 */
export const doseSpotRemoveDefaultPharmacy = oc
  .route({ method: 'DELETE', path: '/v2/dosespot/patients/{patientId}/default-pharmacy' })
  .input(z.object({ patientId: z.string().min(1) }))
  .output(
    z.object({
      /** `false` when there was no stored default — removing nothing is a no-op success. */
      removed: z.boolean(),
    }),
  )
